<template>
  <div class="plan-new-signature">
    <div class="bg-2 rounded-16 p-4">
      <div class="alert">
        <i class="ph ph-warning-circle fs-18 pb-1" />
        <div>
          <div class="fs-14">Atenção</div>
          <div class="text-dark-40 fs-12">
            Cadastre pelo menos um plano para completar o cadastro da sua
            assinatura. Você pode cadastrar quantos planos desejar a qualquer
            momento no gerenciamento do produto.
          </div>
        </div>
      </div>

      <snow-input
        v-model="form.name"
        id="name"
        label="Nome do plano *"
        :rules="rules.name"
      />

      <snow-input
        v-model="form.description"
        class="mt-4 mb-7"
        id="description"
        label="Descrição no checkout *"
        type="textarea"
        rows="4"
        :rules="rules.description"
      />

      <h4 class="text-semibold">Origem das vendas</h4>
      <snow-input v-model="form.sck" class="mt-4 mb-6" id="sck" label="SCK" />

      <h4 class="text-semibold">Precificação do plano</h4>

      <snow-input
        v-model="form.price"
        label="Valor em Real Brasileiro (R$) *"
        money
        class="my-5"
        id="price"
        :rules="rules.price"
      />
      <snow-input
        v-model="form.price_usd"
        label="Valor em Dólar Americano (US$)"
        :disabled="!showForeign"
        :info="getInfo('USD')"
        class="my-5"
        id="price"
        money
        :moneyConfig="{ ...moneyConfig, prefix: 'US$ ' }"
        :rules="rules.foreignPricing"
      />
      <snow-input
        v-model="form.price_eur"
        label="Valor em Euro (€)"
        :disabled="!showForeign"
        :info="getInfo('EUR')"
        class="mt-5 mb-7 pb-2"
        id="price"
        money
        :moneyConfig="{ ...moneyConfig, prefix: '€ ' }"
        :rules="rules.foreignPricing"
      />

      <snow-select
        v-model="form.periodicity"
        :disabled="!!form.id"
        id="periodicity"
        label="Periodicidade *"
        :options="frequencyOptions"
        :rules="rules.periodicity"
      />

      <div class="text-dark-40 mt-3 mb-6 fs-14">
        Escolha quando deverá ocorrer a renovação da assinatura.
      </div>

      <div v-if="showTestDays">
        <snow-input
          v-model="form.test_days"
          id="test_days"
          label="Dias de teste grátis"
          mask="###"
        />

        <div class="text-dark-40 mt-3 mb-7 fs-14">
          Transações de período de teste terão cobrança automática de R$ 1,00.
          Caso esteja tudo certo com a transação, o estorno é feito
          automaticamente, mantendo o período de teste estipulado.
        </div>
      </div>

      <action-dispatcher
        v-if="form.price && frequencyMonths"
        action="sale/getInstallments"
        :key="`${frequencyMonths}${form.price}`"
        :parameters="
          clearObject({
            product_id: $route.params.id,
            installments: frequencyMonths,
            value: form.price
          })
        "
      >
        <snow-select
          v-model="form.installments"
          id="installment"
          label="Periodicidade *"
          :options="installmentOptions"
        />

        <div class="text-dark-40 fs-14 mt-2">
          Para parcelar, a periodicidade da assinatura deve ser pelo menos
          bimestral.
        </div>
      </action-dispatcher>

      <h4 class="text-semibold mt-5">
        Novo preço depois do primeiro pagamento
      </h4>

      <snow-switch
        v-model="promotionalPrice"
        label="Definir novo preço"
        class=" mt-3 mb-5"
      />

      <div v-if="promotionalPrice">
        <snow-input
          v-model="form.next_cycle_price"
          money
          id="price"
          label="Novo preço *"
          :rules="rules.nextCyclePrice"
        />

        <div class="text-dark-40 fs-14 mt-2">
          Defina qual será o novo valor a ser cobrado no seu plano,
          certificando-se que não seja um valor menor que R$4,00.
        </div>
      </div>

      <snow-radio
        v-model="form.billing"
        class="mt-5"
        id="billing"
        label="Forma de cobrança *"
        :options="billingOptions"
        vertical
      />

      <snow-input
        v-if="form.billing === 2"
        v-model="form.num_max_payments"
        class="mt-5"
        type="number"
        id="num_max_payments"
        label="Número máximo de recorrências *"
        :rules="rules.num_max_payments"
      />

      <div class="d-flex w-100 justify-content-end mt-6">
        <snow-button
          :disabled="!isValid"
          @click="savePlan"
          text="Salvar plano"
        />
      </div>
    </div>

    <div class="bg-2 rounded-16 p-4 mt-6">
      <offers-or-plans-container
        :items="plans"
        @delete="onRemove"
        @edit="onEdit"
        isSignature
      />
    </div>
  </div>
</template>

<script>
import OffersOrPlansContainer from "@/components/product/OffersOrPlansContainer";
import SnowRadio from "@/components/snow-ui/SnowRadio.vue";
import SnowSwitch from "@/components/snow-ui/SnowSwitch.vue";
import { ABREVIATION_TO_SYMBOL_MAP } from "@/constants/sales.js";
import { formFields } from "@/functions/helpers.js";
import { maxLength, moreOrEqualTo, required } from "@/functions/validation.js";
import { mapState } from "vuex";
import { clearObject } from "../../functions/helpers";

const fields = [
  "id",
  "name",
  "description",
  "price",
  "price_usd",
  "price_eur",
  "periodicity",
  "installments",
  "next_cycle_price",
  "testing",
  "test_days",
  "billing",
  "num_max_payments",
  "payment_form",
  "status",
  "sck"
];
export default {
  components: {
    OffersOrPlansContainer,
    SnowSwitch
  },
  props: {
    plans: {
      type: Array,
      default: () => []
    },
    productCurrency: {
      type: String,
      default: ""
    },
    planToEdit: {
      type: Number,
      default: null
    },
    product_id: {
      type: Number,
      default: null
    },
    showLoading: {
      type: Boolean,
      default: false
    },
    paymentMethods: {
      type: Array,
      default: () => []
    }
  },
  data() {
    let plan = {
      status: "active",
      installments: 1
    };

    if (Number.isInteger(this.planToEdit)) {
      plan = {
        ...plan,
        ...this.plans[this.planToEdit]
      };
      plan.testing = this.plans[this.planToEdit]?.test_days || 0;
      plan.price = Number(this.plans[this.planToEdit].price);
      plan.billing = this.plans[this.planToEdit].num_max_payments === 0 ? 1 : 2;
    }

    return {
      ...formFields(fields, plan),
      promotionalPrice: false,
      modalDeleteVisible: false,
      indexToEdit: null,
      billingOptions: [
        { id: 1, name: "Até o cliente cancelar" },
        { id: 2, name: "Definir um número máximo" }
      ],
      moneyConfig: {
        decimal: ",",
        thousands: ".",
        precision: 2,
        prefix: "",
        masked: false
      },
      rules: {
        name: [required(), maxLength(64)],
        description: [required(), maxLength(980)],
        price: [required(), moreOrEqualTo(4)],
        foreignPricing: [
          val =>
            !!(!val || val >= 4) ||
            "Caso haja valor, deve ser maior ou igual a 4"
        ],
        periodicity: [required()],
        nextCyclePrice: [required(), moreOrEqualTo(4)],
        num_max_payments: [required(), moreOrEqualTo(1)]
      }
    };
  },
  watch: {
    promotionalPrice(v) {
      if (!v) this.form.next_cycle_price = "";
    },
    "form.billing"(v) {
      if (v === 1) {
        this.form.num_max_payments = 0;
      }
    },
    planToEdit(v) {
      if (Number.isInteger(v)) {
        this.form = {
          ...this.plans[v],
          testing: this.plans[v]?.test_days || 0,
          price: Number(this.plans[v].price),
          billing: this.plans[v].num_max_payments ? 2 : 1,
          test_days: Number(this.plans[v]?.test_days)
        };
      }
    }
  },
  computed: {
    ...mapState("product", ["warranties", "currencies", "frequencyOptions"]),
    ...mapState("sale", ["installments"]),
    canChangeInstallment() {
      return (
        this.form.periodicity === "monthly" ||
        this.form.periodicity === "weekly" ||
        !this.form.price
      );
    },
    paymentFormOptions() {
      return [
        { id: 1, name: "Pagamento à vista" },
        { id: 2, name: "Pagamento sem tarifas para o cliente" },
        { id: 3, name: "Pagamento com tarifas para o cliente" }
      ];
    },
    moneyPrefix() {
      return ABREVIATION_TO_SYMBOL_MAP[this.productCurrency];
    },
    isValid() {
      return (
        this.form.price &&
        this.form.price >= 4 &&
        (!parseFloat(this.form.price_eur) || this.form.price_eur >= 4) &&
        (!parseFloat(this.form.price_usd) || this.form.price_usd >= 4) &&
        this.form.description &&
        this.form.description.length <= 980 &&
        this.form.name &&
        (this.form.next_cycle_price >= 4 || !this.promotionalPrice) &&
        this.form.periodicity &&
        this.form.billing &&
        (this.form.billing === 1 || Number(this.form.num_max_payments))
      );
    },
    frequencyMonths() {
      if (!this.form.periodicity) {
        return 0;
      }
      if (["weekly", "monthly"].includes(this.form.periodicity)) {
        return 1;
      }
      return this.frequencyOptions.find(
        item => item.id === this.form.periodicity
      ).months;
    },
    installmentOptions() {
      const opts = [];
      for (let id in this.installments) {
        opts.push({
          id: parseInt(id),
          name: `Até ${id} parcela${id - 1 ? "s" : ""} de R$ ${
            this.installments[id]
          }`
        });
      }
      return opts;
    },
    showTestDays() {
      const allowedMethods = [1, 2, 4];
      return this.paymentMethods.some(method =>
        allowedMethods.includes(method)
      );
    },
    showForeign() {
      return this.paymentMethods.includes(1);
    }
  },
  methods: {
    getInfo(currency) {
      if (this.showForeign) return "";
      const currencyName = {
        USD: "dólar americano",
        EUR: "euro"
      };
      return `Para habilitar configuração de preço em ${currencyName[currency]} seu checkout precisa aceitar Cartão de Crédito.`;
    },
    clearObject(v) {
      return clearObject(v);
    },
    changeTestingPeriod() {
      this.form.testing = !this.form.testing;
    },
    onEdit(index) {
      this.$emit("edit", index);
    },
    onRemove(index) {
      this.$emit("delete", index);
    },
    toggleModal() {
      this.modalDeleteVisible = !this.modalDeleteVisible;
    },
    clear() {
      const { form, errors } = formFields(fields, {});
      this.form = form;
      this.errors = errors;
      this.indexToEdit = null;
    },
    savePlan() {
      if (this.planToEdit === null) {
        const existPlan = this.plans.find(p => p.name == this.form.name);
        if (existPlan) {
          this.errors.name = ["Já existe um plano com esse nome"];
          window.scrollTo(0, 0);
          return;
        }
        if (this.form.price < 4) {
          this.errors.price = ["O valor não pode ser menor que R$ 4,00"];
          return;
        }
      }
      this.$emit("savePlan", this.form); // When plan is in Editing
      this.clear();
    }
  }
};
</script>

<style lang="scss" scoped>
.plan-new-signature {
  .alert {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 1rem;
    margin-block: 24px;
    border-radius: 1rem;
    background-color: white;
  }
}
</style>
